import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sortByLabel, sortByName, sortByTitle, sortStrings } from "@/lib/sortRows";
import { todayISO } from "@/lib/localDate";
import { roleLabel } from "@/lib/roleLabels";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, STATUS_STYLES } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PagedList from "@/components/PagedList";
import PeopleMultiSelect from "@/components/PeopleMultiSelect";
import { initiativeOwnerOptions, INITIATIVE_OWNER_HANDLES } from "@/lib/initiativeOwners";
import { PlusIcon, ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const P_STATUSES = ["initiation", "planning", "execution", "closed", "hold"];
const P_PRIORITIES = ["high", "medium", "low"];
const I_STATUSES = ["planning", "active", "on_hold", "completed"];

export default function InitiativeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [initiative, setInitiative] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyProj = () => ({
    title: "", description: "", plan: "", status: "initiation",
    priority: "medium", owner_id: (user?.role === "manager" || user?.role === "owner") ? user.id : "",
    start_date: todayISO(), end_date: "",
    entity: "", task_number: "", risk_challenges: "", tech_team: "", func_team: "",
    revised_eta: "", fy_lic_cost: "", imp_cost: "", comments: "",
  });
  const [proj, setProj] = useState(emptyProj());
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addMemberId, setAddMemberId] = useState("");

  const isAdmin = user?.role === "admin";
  // "owner" (the account role) has identical permissions to "manager"
  // everywhere — see is_manager_or_admin() in schema_owner_role.sql.
  const isManager = user?.role === "manager" || user?.role === "owner";
  const isOwner = initiative?.owner_id === user?.id;
  const canManageMembers = isAdmin || isManager || isOwner;

  const load = async () => {
    const [i, p, m, u] = await Promise.all([
      api.get(`/initiatives/${id}`),
      api.get(`/projects?initiative_id=${id}`),
      api.get(`/initiatives/${id}/members`),
      api.get("/users"),
    ]);
    setInitiative(i.data);
    setEditForm({
      title: i.data.title,
      description: i.data.description || "",
      status: i.data.status,
      // Falls back to the single owner_id for a row that predates owner_ids, so
      // opening and saving an old initiative doesn't silently blank its owner.
      owner_ids: (i.data.owner_ids || []).length > 0 ? i.data.owner_ids : [i.data.owner_id].filter(Boolean),
    });
    setProjects(sortByTitle(p.data));
    setMembers(sortByName(m.data));
    setAllUsers(sortByName(u.data));
  };
  useEffect(() => { load(); }, [id]);

  const memberIds = new Set(members.map((m) => m.user_id));
  const addableUsers = allUsers.filter((u) => u._id !== initiative?.owner_id && !memberIds.has(u._id));
  const creatorName = allUsers.find((u) => u._id === initiative?.owner_id)?.name || "Unknown";
  // The assignable Owner field (initiatives.owner_ids). Distinct from owner_id,
  // which is the creator and the id every initiatives RLS policy tests — see
  // supabase/schema_initiative_owners.sql for why the two are separate.
  const ownerIds = (initiative?.owner_ids || []).length > 0
    ? initiative.owner_ids
    : [initiative?.owner_id].filter(Boolean);
  const ownerNames = ownerIds.map((oid) => allUsers.find((u) => u._id === oid)?.name || "Unknown");
  const managers = allUsers.filter((u) => u.role === "manager" || u.role === "owner");

  const addMember = async () => {
    if (!addMemberId) return;
    try {
      await api.post(`/initiatives/${id}/members`, { user_id: addMemberId });
      setAddMemberId("");
      toast.success("Member added");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const saveInitiative = async () => {
    try {
      await api.patch(`/initiatives/${id}`, editForm);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const createProject = async () => {
    if (!proj.title.trim()) { toast.error("Title required"); return; }
    if (!proj.owner_id) { toast.error("Select a project manager"); return; }
    try {
      await api.post("/projects", {
        ...proj, initiative_id: id, owner_id: proj.owner_id,
        start_date: proj.start_date || null, end_date: proj.end_date || null,
        revised_eta: proj.revised_eta || null,
        fy_lic_cost: proj.fy_lic_cost === "" ? null : Number(proj.fy_lic_cost),
        imp_cost: proj.imp_cost === "" ? null : Number(proj.imp_cost),
      });
      toast.success("Project created");
      setProj(emptyProj());
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };


  if (!initiative) return <AppLayout><div className="p-10 text-sm text-zinc-500">Loading...</div></AppLayout>;

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4">
        <Link to="/initiatives" className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-md border border-[#0176D3] bg-[#EFF6FF] text-sm font-semibold text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-colors">
          <ArrowLeftIcon size={18} weight="bold" /> Back to Initiatives
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="trk-uppercase-label">Initiative</div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight truncate">{initiative.title}</h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-testid="initiative-owners-header">
              <span className="trk-uppercase-label">{ownerNames.length > 1 ? "Owners" : "Owner"}</span>
              {ownerNames.length === 0
                ? <span className="text-sm text-zinc-500">—</span>
                : ownerNames.map((n, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 rounded-sm border border-border bg-white text-[#0F172A]">{n}</span>
                  ))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-8 trk-fade-in">
        {/* Edit form */}
        <div className="grid md:grid-cols-3 gap-4 border border-border rounded-sm p-5">
          <div className="md:col-span-1">
            <Label className="trk-uppercase-label mb-2 block">Title</Label>
            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="rounded-sm" data-testid="edit-initiative-title" />
          </div>
          <div className="md:col-span-2">
            <Label className="trk-uppercase-label mb-2 block">Description</Label>
            <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="rounded-sm" rows={2} data-testid="edit-initiative-description" />
          </div>
          <div>
            <Label className="trk-uppercase-label mb-2 block">Status</Label>
            <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
              <SelectTrigger className="rounded-sm" data-testid="edit-initiative-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sortByLabel(I_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Label className="trk-uppercase-label mb-2 block">
              Owner{editForm.owner_ids.length > 1 ? "s" : ""} {editForm.owner_ids.length > 0 && `(${editForm.owner_ids.length})`}
            </Label>
            <PeopleMultiSelect
              users={initiativeOwnerOptions(allUsers, editForm.owner_ids)}
              value={editForm.owner_ids}
              onChange={(ids) => setEditForm({ ...editForm, owner_ids: ids })}
              triggerPlaceholder="Select owner(s)…"
              emptyMessage="None of the accounts allowed to own an initiative exist yet."
              testId="edit-initiative-owners"
            />
            <div className="text-xs text-zinc-500 mt-1.5">
              An initiative can have several owners, but only these accounts are eligible:{" "}
              <span className="font-mono">{INITIATIVE_OWNER_HANDLES.join(", ")}</span>. Naming someone
              here does not by itself give them access — add them under Members below for that.
            </div>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button onClick={saveInitiative} data-testid="save-initiative-edit" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Save changes</Button>
          </div>
        </div>

        {/* Projects */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="trk-uppercase-label">Under this initiative</div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Projects</h2>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-project-in-initiative" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                  <PlusIcon size={14} weight="bold" className="mr-1" /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">New Project</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Title <span className="text-red-600">*</span></Label>
                      <Input value={proj.title} onChange={(e) => setProj({ ...proj, title: e.target.value })} className="rounded-sm" data-testid="project-title-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Entity</Label>
                      <Input value={proj.entity} onChange={(e) => setProj({ ...proj, entity: e.target.value })} placeholder="e.g. Indorama Nigeria" className="rounded-sm" data-testid="project-entity-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Description</Label>
                    <Textarea value={proj.description} onChange={(e) => setProj({ ...proj, description: e.target.value })} className="rounded-sm" rows={2} data-testid="project-description-input" />
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Risk / Challenges</Label>
                    <Textarea value={proj.risk_challenges} onChange={(e) => setProj({ ...proj, risk_challenges: e.target.value })} className="rounded-sm" rows={2} data-testid="project-risk-input" />
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Plan</Label>
                    <Textarea value={proj.plan} onChange={(e) => setProj({ ...proj, plan: e.target.value })} className="rounded-sm" rows={3} data-testid="project-plan-input" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Task Number</Label>
                      <Input value={proj.task_number} onChange={(e) => setProj({ ...proj, task_number: e.target.value })} className="rounded-sm" data-testid="project-task-number-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Status</Label>
                      <Select value={proj.status} onValueChange={(v) => setProj({ ...proj, status: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {sortByLabel(P_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Priority</Label>
                      <Select value={proj.priority} onValueChange={(v) => setProj({ ...proj, priority: v })}>
                        <SelectTrigger className="rounded-sm" data-testid="project-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {sortStrings(P_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Tech Team</Label>
                      <Input value={proj.tech_team} onChange={(e) => setProj({ ...proj, tech_team: e.target.value })} className="rounded-sm" data-testid="project-tech-team-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Func Team</Label>
                      <Input value={proj.func_team} onChange={(e) => setProj({ ...proj, func_team: e.target.value })} className="rounded-sm" data-testid="project-func-team-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Project Manager <span className="text-red-600">*</span></Label>
                    <Select value={proj.owner_id} onValueChange={(v) => setProj({ ...proj, owner_id: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="project-manager-select"><SelectValue placeholder="Select project manager" /></SelectTrigger>
                      <SelectContent>
                        {managers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Start Date</Label>
                      <Input type="date" value={proj.start_date} onChange={(e) => setProj({ ...proj, start_date: e.target.value })} className="rounded-sm" data-testid="project-start-date-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">ETA</Label>
                      <Input type="date" value={proj.end_date} onChange={(e) => setProj({ ...proj, end_date: e.target.value })} className="rounded-sm" data-testid="project-eta-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Revised ETA</Label>
                      <Input type="date" value={proj.revised_eta} onChange={(e) => setProj({ ...proj, revised_eta: e.target.value })} className="rounded-sm" data-testid="project-revised-eta-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">FY Lic Cost $</Label>
                      <Input type="number" step="0.01" value={proj.fy_lic_cost} onChange={(e) => setProj({ ...proj, fy_lic_cost: e.target.value })} className="rounded-sm" data-testid="project-fy-lic-cost-input" />
                    </div>
                    <div>
                      <Label className="trk-uppercase-label mb-2 block">Imp Cost $</Label>
                      <Input type="number" step="0.01" value={proj.imp_cost} onChange={(e) => setProj({ ...proj, imp_cost: e.target.value })} className="rounded-sm" data-testid="project-imp-cost-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="trk-uppercase-label mb-2 block">Comments</Label>
                    <Textarea value={proj.comments} onChange={(e) => setProj({ ...proj, comments: e.target.value })} className="rounded-sm" rows={2} data-testid="project-comments-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createProject} data-testid="save-project-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {projects.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-8 text-center text-sm text-zinc-500">
              No projects under this initiative yet.
            </div>
          ) : (
            <PagedList items={projects} pageSize={12} testIdPrefix="initiative-projects">
              {(rows) => (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rows.map((p) => (
                <Link key={p._id} to={`/projects/${p._id}`} className="trk-card trk-hover-lift p-5 flex flex-col justify-between min-h-[140px]">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <StatusBadge status={p.status} />
                      <ArrowRightIcon size={16} className="text-zinc-400" />
                    </div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{p.title}</h3>
                    <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{p.description || "—"}</p>
                  </div>
                </Link>
              ))}
                </div>
              )}
            </PagedList>
          )}
        </div>

        {/* Members — who can see this initiative outright (a project member
            under it can already see it transitively, via "read initiatives"
            RLS, without needing a row here — this is for people who should
            see the initiative itself, e.g. before any project exists). */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="trk-uppercase-label">Access</div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Members</h2>
            </div>
            {canManageMembers && (
              <div className="flex items-center gap-2">
                <Select value={addMemberId} onValueChange={setAddMemberId}>
                  <SelectTrigger className="rounded-sm w-56" data-testid="add-initiative-member-select">
                    <SelectValue placeholder="Add a member…" />
                  </SelectTrigger>
                  <SelectContent>
                    {addableUsers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name} — {roleLabel(u.role).toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={addMember} disabled={!addMemberId} data-testid="add-initiative-member-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                  <PlusIcon size={14} weight="bold" className="mr-1" /> Add
                </Button>
              </div>
            )}
          </div>

          <div className="border border-border rounded-sm overflow-hidden">
            <div className="divide-y divide-border">
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-50/60">
                <div className="min-w-0">
                  <div className="font-medium">{creatorName}</div>
                  {/* Deliberately "Creator", not "Owner": this row is
                      initiatives.owner_id, the id RLS checks, which is now a
                      different thing from the assignable Owner field above. */}
                  <div className="text-xs text-zinc-500">Creator · always has access</div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Can't be removed</span>
              </div>
              {members.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-zinc-500">
                  No other members yet. {canManageMembers && "Add someone above so they can see this initiative directly."}
                </div>
              ) : (
                <PagedList items={members} pageSize={15} testIdPrefix="initiative-members" className="px-4 pb-3">
                  {(rows) => (
                    <div className="-mx-4">
                {rows.map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between px-4 py-3" data-testid={`initiative-member-row-${m.user_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-zinc-500">{m.email} · {roleLabel(m.role).toUpperCase()}</div>
                    </div>
                  </div>
                ))}
                    </div>
                  )}
                </PagedList>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
